import { useCallback, useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import CustomLink from "@Components/CustomLink";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import Reveal from "@Components/Reveal/Reveal";
import wrapperHOC from "@Utils/wrapperHOC";
import { Card } from "./cards";
import styles from "./Testimonials.module.scss";

/**
 * One horizontal rail of cards, with its own heading, dots and chevrons.
 *
 * ⚠️  THE CAROUSEL STATE IS PER-RAIL, which is the whole reason this is a
 * component and not a loop. `/list` draws two of these now — partner quotes and
 * case studies — and each needs its own scroll position, dot index and chevron
 * enablement. The measuring also has to be per-rail: `cardPitch` divides by the
 * number of cards IN THIS RAIL, and three quotes and four studies do not share a
 * pitch.
 *
 * An internal part, never a page section on its own — `Testimonials` and
 * `CaseStudies` are what the page mounts. It is still wrapperHOC-wrapped, as
 * every default export here must be, and carries `showForChina: true` to match
 * both parents; the default `false` would blank the rails on the China build
 * while their wrappers still rendered.
 *
 * It still renders either card kind, so a rail handed a mixed list would work;
 * nothing does that any more.
 */
const EDGE = 1;

/**
 * Drawn here rather than imported. The exported icons bake in the states the
 * design happened to be showing — a pale #c9c9c9 back chevron beside a dark
 * #202020 forward one — so as flat images the back one stayed greyed out even
 * once it was usable. Taking the stroke from currentColor hands that decision
 * to the button, which knows whether it is disabled.
 */
const Chevron = ({ back = false }: { back?: boolean }) => (
  <svg
    className={styles.chev}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d={back ? "M11 2L5 8L11 14" : "M5 2L11 8L5 14"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * How far one card advances the track: its width plus the gap after it.
 * `scrollWidth / count` is a card short of a gap — the track has one fewer gap
 * than it has cards — which left the last stop a few pixels shy of the end.
 */
const cardPitch = (node: HTMLDivElement, count: number) => {
  const track = node.firstElementChild;
  const gap = track ? parseFloat(window.getComputedStyle(track).columnGap) || 0 : 0;
  return (node.scrollWidth + gap) / count;
};

interface RailProps {
  /** Translation key for the section's own heading. */
  heading: string;
  /** The cards this rail carries — all of one kind. */
  items: Card[];
  /**
   * Draw a hairline above this rail, separating it from the one before.
   * Set by the second of two rails sharing a band — see `CaseStudies`.
   */
  divided?: boolean;
}

const Rail = ({ heading, items, divided = false }: RailProps) => {
  // Resolves the card copy AND the analytics label. The label matters as much as
  // the copy: the old page reported this event with `t(item.name)`, so it has to
  // resolve the same way or the existing "clicked_case_study" breakdown gains a
  // second set of rows keyed by the raw Tolgee key.
  const { t } = useTranslationHook();

  // Same event the old page's case study cards fired, so the existing
  // "clicked_case_study" reporting keeps working across the swap.
  const handleStudyClick = (nameKey: string) => {
    const name = t(nameKey);
    gtagEventEmitter({ action: "clicked_case_study", category: "Case Study", label: name });
  };

  const viewport = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // 0 while every card is on screen — that is when the controls are inert.
  const [maxIndex, setMaxIndex] = useState(0);
  const maxIndexRef = useRef(0);
  // The chevrons read the track's real position rather than the dot index. The
  // viewport scrolls natively, so a trackpad swipe moves the cards without ever
  // going through goTo — driving the chevrons off the index left the back one
  // greyed out while the track was plainly scrolled in from the start.
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  /** Pulls the dot index and both chevron states off wherever the track sits. */
  const sync = useCallback(() => {
    const node = viewport.current;
    if (!node) return;

    const furthest = node.scrollWidth - node.clientWidth;
    setAtStart(node.scrollLeft <= EDGE);
    setAtEnd(node.scrollLeft >= furthest - EDGE);

    const pitch = cardPitch(node, items.length);
    if (pitch <= 0) return;
    const nearest = Math.round(node.scrollLeft / pitch);
    setIndex(Math.min(Math.max(nearest, 0), maxIndexRef.current));
  }, [items.length]);

  const measure = useCallback(() => {
    const node = viewport.current;
    if (!node) return;
    const pitch = cardPitch(node, items.length);
    const visible = pitch > 0 ? Math.round(node.clientWidth / pitch) : items.length;
    maxIndexRef.current = Math.max(0, items.length - visible);
    setMaxIndex(maxIndexRef.current);
    sync();
  }, [sync, items.length]);

  useEffect(() => {
    const node = viewport.current;
    measure();
    window.addEventListener("resize", measure);

    // Throttled to a frame: a swipe fires scroll far more often than the two
    // booleans and one index can actually change.
    let raf = 0;
    const onScroll = () => {
      if (!raf)
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          sync();
        });
    };
    if (node) node.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", measure);
      if (raf) window.cancelAnimationFrame(raf);
      if (node) node.removeEventListener("scroll", onScroll);
    };
  }, [measure, sync]);

  const goTo = (next: number) => {
    const node = viewport.current;
    const clamped = Math.min(Math.max(next, 0), maxIndex);
    setIndex(clamped);
    if (node) {
      const reduced =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      node.scrollTo({
        left: cardPitch(node, items.length) * clamped,
        behavior: reduced ? "auto" : "smooth",
      });
    }
  };

  return (
    <section className={`${styles.section} ${divided ? styles.sectionDivided : ""}`}>
      <div className={`${styles.inner} ${divided ? styles.innerDivided : ""}`}>
        <Reveal as="h2" className={styles.heading}>
          {t(heading)}
        </Reveal>

        <div className={styles.viewport} ref={viewport}>
          <div className={styles.track}>
            {items.map((item) =>
              item.kind === "quote" ? (
                <article className={styles.card} key={item.id}>
                  <div className={styles.cardTop}>
                    <div className={styles.person}>
                      <Image
                        src={item.avatar}
                        alt=""
                        className={styles.avatar}
                        width={44}
                        height={44}
                        isNotLazy
                      />
                      <div className={styles.personText}>
                        <span className={styles.name}>{item.name}</span>
                        <span className={styles.role}>{t(item.role)}</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.cardBottom}>
                    <p className={styles.quote}>&ldquo;{t(item.quote)}&rdquo;</p>
                  </div>
                </article>
              ) : (
                <article className={styles.card} key={item.id}>
                  {/* Figma 3233:595. The hero is inset 8px inside the card's 16px
                      radius there and carries its own 12px one; it bleeds to the card
                      edge here and lets `.card` do the clipping.

                      ⚠️  ONE IMAGE, NOT TWO LAYERS. This was a gradient ground with a
                      white lockup PNG centred on it and inverted for contrast. The
                      supplied artwork already has the "amber | X" lockup set into the
                      scene, so the composition — and the invert it needed — is gone. */}
                  <div className={styles.studyTop}>
                    <div
                      className={styles.studyHero}
                      style={{ backgroundImage: `url(${item.cover})` }}
                    />

                    <div className={styles.studyText}>
                      <p className={styles.studyTitle}>{t(item.title)}</p>
                      <p className={styles.studySummary}>{t(item.summary)}</p>
                    </div>
                  </div>

                  <div className={styles.cardBottom}>
                    <CustomLink
                      href={item.pdf}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => handleStudyClick(item.nameKey)}
                      className={styles.readMore}
                    >
                      {t("list.casestudies.readCaseStudy")}
                    </CustomLink>
                  </div>
                </article>
              ),
            )}
          </div>
        </div>

        {/* ⚠️  NO CONTROLS WHEN THERE IS NOTHING TO CONTROL. `maxIndex` is 0 while
            every card is already on screen, which is the partner-quotes rail at
            desktop width — three cards, three visible. It used to render anyway:
            a dot per card plus two permanently disabled chevrons, a row of
            controls advertising a carousel that could not move.

            It is per-rail and per-width, not a prop: the same three quotes DO
            overflow on a narrower screen, and the controls come back on their
            own when they do. */}
        {maxIndex > 0 && (
          <div className={styles.nav}>
            <div className={styles.dots}>
              {/* One per stop the track can rest at, not per card — with five
                  cards and three on screen there are three stops, so a dot per
                  card would leave two of them permanently unreachable. Falls back
                  to one per card when everything fits and the controls are inert,
                  which is the idle row the design draws. */}
              {Array.from({ length: maxIndex > 0 ? maxIndex + 1 : items.length }, (_, i) => (
                <button
                  type="button"
                  key={`dot-${i}`}
                  aria-label={t("list.v2.testimonials.goto", { index: String(i + 1) })}
                  className={`${styles.dot} ${i === index ? styles.dotActive : ""}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>

            <div className={styles.discs}>
              <button
                type="button"
                aria-label={t("list.v2.testimonials.previous")}
                className={styles.disc}
                onClick={() => goTo(index - 1)}
                disabled={atStart}
              >
                <Chevron back />
              </button>
              <button
                type="button"
                aria-label={t("list.v2.testimonials.next")}
                className={styles.disc}
                onClick={() => goTo(index + 1)}
                disabled={atEnd}
              >
                <Chevron />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
export default wrapperHOC(Rail, {
  componentName: "Rail-ListWithUs",
  showForChina: true,
});
